import { type InputHTMLAttributes, type Ref, useId } from 'react';

import { useTranslation } from 'react-i18next';

import styles from './Field.module.css';
import { ErrorIcon } from './icons';

export interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'aria-invalid' | 'aria-describedby'> {
  /** Rotulo sempre associado ao controle por `htmlFor`, nunca so por proximidade. */
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly ref?: Ref<HTMLInputElement>;
}

export function Field({
  label,
  hint,
  error,
  required = false,
  className,
  ...rest
}: FieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasError = typeof error === 'string' && error.length > 0;

  const describedBy = [hint ? hintId : null, hasError ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? (
          <>
            <span aria-hidden="true">{' *'}</span>
            <span className="visually-hidden">{` (${t('field.required')})`}</span>
          </>
        ) : null}
      </label>

      <input
        id={id}
        className={[styles.input, className].filter(Boolean).join(' ')}
        required={required}
        aria-invalid={hasError ? true : undefined}
        aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        {...rest}
      />

      {hint ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}

      {hasError ? (
        <p id={errorId} className={styles.error} role="alert">
          <ErrorIcon className={styles.errorIcon} />
          <span className="visually-hidden">{`${t('field.errorIcon')}: `}</span>
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}
